<template>
	<div tabindex="0" v-on:keydown.down.prevent v-on:keydown.up.prevent class="tasks tasks--editor">
		<div
			class="tasks__section tasks__section--week"
			v-for="(week, weekIndex) in editorWeeks"
			:key="weekIndex">
			<!-- Week Header -->
			<div class="tasks__section__header tasks__section__header--week">
				<div
					class="tasks__section__header__title tasks__section__header__title--week">
					Week {{week.week}}<br>
					<span class="tasks__section__header__title__date">{{new Date(week.date).format('d/m -')}} {{new Date(week.end).format('d/m')}}</span>
				</div>
			</div>
			<div class="tasks__section__content tasks__section__content--week">
				<!-- Existing Tasks -->
				<div
					v-for="(task, taskIndexInWeek) in getWeekTasks(week, planning.Tasks, { unsorted: true })"
					:key="task.id">
					<div
						class="task task--parent">
						<div class="task__cell task__open-children">
							<button type="button" class="task__open-children__button" @click="toggleChildTasks(task.index, getIndexOnPage(weekIndex, taskIndexInWeek))" tabindex="-1">
								<i v-if="!childTasksOpen[task.index]" class="fas fa-chevron-circle-right"></i>
								<i v-else class="fas fa-chevron-circle-down"></i>
							</button>
						</div>
						<div class="task__cell task__type">
							<Dropdown
								class="task__type__dropdown"
								:typable="true"
								:placeholder="'Task type'"
								v-model="task.type"
								:options="['Read', 'Summarize', 'Do', 'Study', 'Watch']"
								:labels="['Read', 'Summarize', 'Do', 'Study', 'Watch']">
							</Dropdown>
						</div>
						<div class="task__cell task__description">
							<input
								class="task__description__input"
								type="text"
								v-model="task.description"
								placeholder="Task description"/>
						</div>
						<div v-if="planning.Topics.length > 0" class="task__cell task__topic">
							<Dropdown
								class="task__topic__dropdown"
								:faint="true"
								:placeholder="'Topic'"
								v-model="task.topicIndex"
								:options="[-1].concat(planning.Topics.map((topic, index) => index))"
								:labels="['No topic'].concat(planning.Topics.map(topic => topic.title))">
							</Dropdown>
						</div>
						<div class="task__cell task__day">
							<Dropdown
								class="task__day__dropdown"
								:faint="true"
								:placeholder="'Day of week'"
								v-model="task.Info.date"
								:options="getDaysOfWeek(week.date, ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'])"
								:labels="['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']">
							</Dropdown>
						</div>
						<div class="task__cell task__delete">
							<button class="task__delete__button" type="button" @click="deleteTask(task.id, task.index)" tabindex="-1">
								<i class="fas fa-trash-alt"></i>
							</button>
						</div>
					</div>
					<!-- Child Task section -->
					<div class="task__children" ref="children" :style="{'height': childTasksHeights[task.index]}">
						<div class="task__children__content" ref="childrenContent">
							<div
								v-for="(childTask, childIndex) in task.ChildTasks" :key="childTask.id"
								class="task task--child task--child--editor">
								<div class="task__cell task__open-children">
								</div>
								<div class="task__cell task__type">
									{{task.type}}
								</div>
								<div class="task__cell task__description">
									<input
										class="task__description__input"
										type="text"
										v-model="childTask.description"
										placeholder="Task description"/>
								</div>
								<div class="task__cell task__topic">
									{{planning.Topics.map(topic => topic.title)[task.topicIndex] || 'No topic'}}
								</div>
								<div class="task__cell task__day">
									{{getDayName(childTask.Info.date)}}
								</div>
								<div class="task__cell task__delete">
									<button class="task__delete__button" type="button" @click="deleteChildTask(task.index, childIndex, getIndexOnPage(weekIndex, taskIndexInWeek))" tabindex="-1">
										<i class="fas fa-trash-alt"></i>
									</button>
								</div>
							</div>
							<div class="task">
								<div class="task__cell task__open-children">
								</div>
								<div class="task__cell task__type">
									<!-- Used for tabbing -->
									<div :tabindex="taskStartsOpen ? 0 : -1" ref="task__child__start" @blur="taskStartsOpen = false"></div>
									{{task.type}}
								</div>
								<div class="task__cell task__description">
									<input
										class="task__description__input"
										type="text"
										v-model="task.newChildTask.description"
										@keydown.enter.prevent="addChildTask(task.index, task.newChildTask, getIndexOnPage(weekIndex, taskIndexInWeek))"
										placeholder="Subtask description"/>
								</div>
								<div v-if="planning.Topics.length > 0" class="task__cell task__topic">
									{{planning.Topics.map(topic => topic.title)[task.topicIndex] || 'No topic'}}
								</div>
								<div class="task__cell task__day">
									{{getDayName(task.Info.date)}}
								</div>
								<div class="task__cell task__add">
									<button
										class="task__add__button"
										type="button"
										@keydown.enter.prevent="addChildTask(task.index, task.newChildTask, getIndexOnPage(weekIndex, taskIndexInWeek))"
										@click="addChildTask(task.index, task.newChildTask, getIndexOnPage(weekIndex, taskIndexInWeek))">
										Add
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
				<!-- New Task Section -->
				<div class="task">
					<div class="task__cell task__open-children">
					</div>
					<div class="task__cell task__type">
						<!-- Used for tabbing -->
						<div :tabindex="taskStartsOpen ? 0 : -1" ref="task__start" @blur="taskStartsOpen = false"></div>
						<Dropdown
							class="task__type__dropdown"
							:brick="false"
							:placeholder="'Task type'"
							v-model="week.newTask.type"
							:options="['Read', 'Summarize', 'Do', 'Study', 'Watch']"
							:labels="['Read', 'Summarize', 'Do', 'Study', 'Watch']">
						</Dropdown>
					</div>
					<div class="task__cell task__description">
						<input
							class="task__description__input"
							type="text"
							v-model="week.newTask.description"
							placeholder="Task description"/>
					</div>
					<div v-if="planning.Topics.length > 0" class="task__cell task__topic">
						<Dropdown
							class="task__topic__dropdown"
							:faint="true"
							:placeholder="'Topic'"
							v-model="week.newTask.topicIndex"
							:options="[-1].concat(planning.Topics.map((topic, index) => index))"
							:labels="['No topic'].concat(planning.Topics.map(topic => topic.title))">
						</Dropdown>
					</div>
					<div class="task__cell task__day">
						<Dropdown
							class="task__day__dropdown"
							:faint="true"
							:placeholder="'Day of week'"
							v-model="week.newTask.day"
							:options="['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']"
							:labels="['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']">
						</Dropdown>
					</div>
					<div class="task__cell task__add">
						<button
							class="task__add__button"
							type="button"
							@keydown.enter.prevent="addTask(weekIndex, getDayOfWeek(week.date, week.newTask.day), week.newTask)"
							@click="addTask(weekIndex, getDayOfWeek(week.date, week.newTask.day), week.newTask)">
							Add
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import DateService from '@/services/DateService'
import Dropdown from '@/components/Dropdown.vue'
import { tasks } from '@/components/mixins/tasks'
export default {
	mixins: [tasks],
	components: {
		Dropdown
	},
	props: {
		planning: Object,
		editorWeeks: Array
	},
	data () {
		return {
			childTasksOpen: new Array(this.planning.Tasks.length).fill(false),
			childTasksHeights: new Array(this.planning.Tasks.length).fill(0),
			taskStartsOpen: false
		}
	},
	methods: {
		addTask (weekIndex, date, task) {
			var validationMessage = ''
			if (!task.type) validationMessage = 'Please specify a type for the task you are trying to add.'
			else if (!task.description) validationMessage = 'Please specify a description for the task you are trying to add.'
			if (!task.type || !task.description) {
				this.$store.dispatch('openPopup', { title: 'Task creation', message: validationMessage	})
				return
			}
			this.$emit('addTask', weekIndex, date, task)
			// Tabbing
			this.taskStartsOpen = true
			this.$refs.task__start[weekIndex].focus()
			this.childTasksOpen.push(false)
			this.childTasksHeights.push(0)
		},
		deleteTask (id, index) {
			this.$emit('deleteTask', id)
			this.$delete(this.childTasksOpen, index)
			this.$delete(this.childTasksHeights, index)
		},
		addChildTask (parentIndex, childTask, parentIndexOnPage) {
			if (!childTask.description) {
				var validationMessage = 'Please specify a description for the task you are trying to add.'
				this.$store.dispatch('openPopup', { title: 'Task creation', message: validationMessage	})
				return
			}
			this.$emit('addChildTask', parentIndex, childTask)
			// Tabbing
			this.taskStartsOpen = true
			this.$refs.task__child__start[parentIndexOnPage].focus()
			this.$nextTick(function () {
				this.$set(this.childTasksHeights, parentIndex, !this.childTasksOpen[parentIndex] ? 0 : this.$refs.childrenContent[parentIndexOnPage].clientHeight + 'px')
			})
		},
		deleteChildTask (parentIndex, childIndex, parentIndexOnPage) {
			this.$emit('deleteChildTask', parentIndex, childIndex)
			this.$nextTick(() => {
				this.$set(this.childTasksHeights, parentIndex, !this.childTasksOpen[parentIndex] ? 0 : this.$refs.childrenContent[parentIndexOnPage].clientHeight + 'px')
			})
		},
		toggleChildTasks (index, indexOnPage) {
			this.$set(this.childTasksOpen, index, !this.childTasksOpen[index])
			this.$set(this.childTasksHeights, index, !this.childTasksOpen[index] ? 0 : this.$refs.childrenContent[indexOnPage].clientHeight + 'px')
		},
		getDayName (date) {
			return DateService.getDayName(date)
		},
		getIndexOnPage (weekIndex, taskIndexInWeek) {
			let indexOnPage = 0
			let w = 0
			while (w < weekIndex) {
				indexOnPage += this.getWeekTasks(this.editorWeeks[w], this.planning.Tasks).length
				w++
			}
			indexOnPage += taskIndexInWeek
			return indexOnPage
		}
	}
}
</script>

<style lang="scss" scoped>
@import "@/styles/components/_tasks.scss";
@import "@/styles/components/_task.scss";
</style>
